import React from 'react';
import { motion } from 'framer-motion';
import { Play, MapPin, CalendarClock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/Button';
import { useCountdown } from '../hooks/useCountdown';
import { SEASON_2_PREMIERE_ISO, season2Premiere, type Season2Episode } from '../data/season2Episodes';
import { SPOTIFY_SHOW_URL } from '../data/season1Episodes';
interface Season2AnnouncementProps {
  showDetailsLink?: boolean;
  episode?: Season2Episode;
}
const premiereDate = new Date(SEASON_2_PREMIERE_ISO);
const premiereDay = premiereDate.toLocaleDateString('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  timeZone: 'America/New_York'
});
export function Season2Announcement({
  showDetailsLink = true,
  episode = season2Premiere
}: Season2AnnouncementProps) {
  const isNewRelease = episode.id > 1;
  const {
    days,
    hours,
    minutes,
    seconds,
    isLive
  } = useCountdown(SEASON_2_PREMIERE_ISO);
  const units = [{
    label: 'Days',
    value: days
  }, {
    label: 'Hours',
    value: hours
  }, {
    label: 'Min',
    value: minutes
  }, {
    label: 'Sec',
    value: seconds
  }];
  return <section aria-labelledby="season2-heading" className="relative bg-dark-900 border-y border-gold-500/30 py-20 md:py-28 overflow-hidden" data-id="element-139" data-mp="ma5atmlp9yvk">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-140" data-mp="w21dzc2n8f6n">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center" data-id="element-141" data-mp="3ujf27km7obcm">
          {/* Copy + countdown */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="lg:col-span-7 order-2 lg:order-1" data-id="element-142" data-mp="3ckr0txslqht">
            <p className="flex items-center gap-2 text-gold-500 font-semibold mb-5" data-id="element-143" data-mp="1jrm5wkgryms2">
              <span className="relative flex h-2.5 w-2.5" data-id="element-144" data-mp="1oqfb7plwr7h5">
                <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-gold-500 opacity-75" data-id="element-145" data-mp="1oovvc045yyfw"></span>
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gold-500" data-id="element-146" data-mp="3fuh6ybexgtvv"></span>
              </span>
              {isNewRelease ? 'New release · Now streaming' : isLive ? 'Now streaming' : 'The wait is almost over'}
            </p>

            <h2 id="season2-heading" className="text-5xl md:text-7xl font-serif font-bold text-white leading-[1.05] mb-6" data-id="element-147" data-mp="322kkonzxxe7e">
              {isNewRelease ? <>
                  {episode.title}
                  <span className="text-gold-500" data-id="element-148" data-mp="3axifk0mnf11d">.</span>
                </> : isLive ? <>
                  Season 2 <span className="text-gold-500" data-id="element-149" data-mp="2ieuzx0qf8e7o">is here.</span>
                </> : <>
                  Season 2 <span className="text-gold-500" data-id="element-150" data-mp="2ecq7u6fmfpn5">premieres</span>
                  <br data-id="element-151" data-mp="249umx3sdbthq" />
                  {premiereDay}.
                </>}
            </h2>

            <p className="flex items-center gap-2 text-lg md:text-xl text-white/80 mb-10" data-id="element-152" data-mp="290t7ifke3dqs">
              <CalendarClock className="w-5 h-5 text-gold-500 shrink-0" data-id="element-153" data-mp="1ubn6cm1zrdg9" />
              {isNewRelease ? `Season 2 · Episode ${episode.id} with ${episode.guest}${episode.duration ? ` · ${episode.duration}` : ''}` : '3:00 PM Eastern · Spotify, Apple Podcasts & everywhere you listen'}
            </p>

            {!isLive && <div className="grid grid-cols-4 gap-3 max-w-lg mb-10" role="timer" aria-label={`${days} days, ${hours} hours, ${minutes} minutes until Season 2`} data-id="element-154" data-mp="2rydt9druxw76">
                {units.map(unit => <div key={unit.label} className="rounded-xl bg-dark-800 border border-dark-600 py-4 text-center" data-id="element-155" data-mp="11lod0egrku5s">
                    <p className="text-3xl md:text-5xl font-serif font-bold text-white tabular-nums" data-id="element-156" data-mp="1mtgzh5wvwlta">
                      {String(unit.value).padStart(2, '0')}
                    </p>
                    <p className="text-xs uppercase tracking-wider text-white/50 mt-1" data-id="element-157" data-mp="1d2w3lq8lhve2">
                      {unit.label}
                    </p>
                  </div>)}
              </div>}

            {/* Episode teaser */}
            <div className="border-t border-dark-600 pt-8" data-id="element-158" data-mp="25rb65dajil8i">
              {!isNewRelease && <>
                  <p className="text-sm text-white/50 mb-2" data-id="element-159" data-mp="20qncskyu9sb4">
                    Episode {episode.id} · {episode.guest}'s Story
                  </p>
                  <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3" data-id="element-160" data-mp="1qvbxo1jiqj2u">
                    {episode.title}
                  </h3>
                </>}
              <p className="text-white/80 text-lg leading-relaxed mb-8 max-w-2xl" data-id="element-161" data-mp="fevsd11ohitp">
                {episode.hook}
              </p>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-162" data-mp="2r41v6g8m3cib">
                <a href={isLive || isNewRelease ? episode.spotifyUrl : SPOTIFY_SHOW_URL} target="_blank" rel="noopener noreferrer" data-id="element-163" data-mp="2w1aguqsyxyob">
                  <Button size="lg" leftIcon={<Play className="w-5 h-5 fill-current" data-id="element-165" data-mp="3arhoebhftxa8" />} data-id="element-164" data-mp="35t7t80vpqwom">
                    {isLive || isNewRelease ? 'Listen Now' : 'Follow to Get Notified'}
                  </Button>
                </a>
                {showDetailsLink && <Link to="/podcasts/season-2" data-id="element-166" data-mp="3oqr83l4ur9iy">
                    <Button size="lg" variant="outline" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-168" data-mp="1pfpzae9c0zmh" />} data-id="element-167" data-mp="3ooepd5r9sv4u">
                      Explore Season 2
                    </Button>
                  </Link>}
              </div>
            </div>
          </motion.div>

          {/* Image */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="lg:col-span-5 order-1 lg:order-2" data-id="element-169" data-mp="1ibrqct3fnbup">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-dark-600" data-id="element-170" data-mp="258tjiq9w11iw">
              <img src={episode.image} alt={episode.imageAlt} className="w-full h-full object-cover" data-id="element-171" data-mp="218suyct2r9y4" />
              {(isLive || isNewRelease) && <a href={episode.spotifyUrl} target="_blank" rel="noopener noreferrer" aria-label={`Listen to Season 2 Episode ${episode.id}: ${episode.title}`} className="absolute inset-0 flex items-center justify-center group" data-id="element-172" data-mp="2a3ftqq48go1e">
                  <span className="w-20 h-20 rounded-full bg-gold-500 flex items-center justify-center text-dark-900 shadow-lg shadow-gold-500/30 transition-transform duration-150 group-hover:scale-105" data-id="element-173" data-mp="37nfrqacalqm6">
                    <Play className="w-8 h-8 fill-current ml-1" data-id="element-174" data-mp="2ylgbvld0kskt" />
                  </span>
                </a>}
              <div className="absolute inset-x-0 bottom-0 bg-dark-900/85 backdrop-blur-sm p-6" data-id="element-175" data-mp="2jwjw124pub9n">
                <p className="text-gold-500 text-sm font-semibold mb-1" data-id="element-176" data-mp="2oov44s9m13da">
                  S2 · EP {episode.id}
                </p>
                <p className="text-white font-serif text-xl font-bold mb-1" data-id="element-177" data-mp="2auke87lvn35n">
                  {episode.guest}
                </p>
                <p className="flex items-center text-white/60 text-sm" data-id="element-178" data-mp="20xz5bb6ql7wj">
                  <MapPin className="w-3.5 h-3.5 mr-1" data-id="element-179" data-mp="210tqj4xkqwr0" />
                  From {episode.hometown}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}